import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Plus, Trash2, Trophy, Percent, Target } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import prizeStructureService from '../../services/prizeStructureService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const InputField = styled(Input)`
  width: 100%;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlacementsSection = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #2d2d44;
`;

const PlacementsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PlacementsTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddPlacementButton = styled.button`
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const PlacementsTable = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #3d3d5a;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 1fr 60px;
  gap: 15px;
  padding: 15px 20px;
  background: linear-gradient(90deg, #3d3d5a 0%, #2d2d44 100%);
  border-bottom: 1px solid #3d3d5a;
`;

const TableHeaderCell = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 1fr 60px;
  gap: 15px;
  padding: 15px 20px;
  border-bottom: 1px solid #3d3d5a;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlacementInput = styled(Input)`
  width: 100%;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  border: none;
  border-radius: 6px;
  padding: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const TotalPercent = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #3d3d5a;
  border-radius: 12px;
  text-align: center;
`;

const TotalPercentText = styled.div`
  color: #b8b8d1;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
`;

const TotalPercentValue = styled.div`
  color: ${props => props.isValid ? '#4ecdc4' : '#ff6b6b'};
  font-size: 24px;
  font-weight: 700;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  padding: 25px;
  border-top: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const PrizeStructureModal = ({ 
  show, 
  prizeStructure, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    organizerCutPercent: 0,
    placements: [{ row: '', percent: '', score: '' }]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show) {
      if (prizeStructure) {
        setFormData({
          id: prizeStructure.id || '',
          name: prizeStructure.name || '',
          organizerCutPercent: prizeStructure.organizerCutPercent || 0,
          placements: prizeStructure.placements && prizeStructure.placements.length > 0 
            ? prizeStructure.placements.map(p => ({ row: p.row || '', percent: p.percent || '', score: p.score || '' }))
            : [{ row: '', percent: '', score: '' }]
        });
      } else {
        setFormData({
          name: '',
          organizerCutPercent: 0,
          placements: [{ row: '', percent: '', score: '' }]
        });
      }
      setError('');
      setSuccess('');
    }
  }, [show, prizeStructure]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlacementChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      placements: prev.placements.map((placement, i) =>
        i === index ? { ...placement, [field]: value } : placement
      )
    }));
  };

  const addPlacement = () => {
    setFormData(prev => ({
      ...prev,
      placements: [...prev.placements, { row: '', percent: '', score: '' }]
    }));
  };

  const removePlacement = (index) => {
    if (formData.placements.length > 1) {
      setFormData(prev => ({
        ...prev,
        placements: prev.placements.filter((_, i) => i !== index)
      }));
    }
  };

  const totalPercent = Number(formData.organizerCutPercent) + 
    formData.placements.reduce((sum, p) => sum + Number(p.percent || 0), 0);
  
  const isValid = formData.name.trim() && 
    formData.organizerCutPercent !== '' &&
    formData.placements.every(p => p.row && p.percent !== '' && p.score !== '') &&
    totalPercent === 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setError('لطفاً تمام فیلدها را پر کنید و مجموع درصدها باید 100 باشد');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        organizerCutPercent: Number(formData.organizerCutPercent),
        placements: formData.placements.map(p => ({
          row: Number(p.row),
          percent: Number(p.percent),
          score: Number(p.score)
        }))
      };

      if (prizeStructure) {
        await prizeStructureService.updatePrizeStructure({ ...payload, id: formData.id });
        toast.success('ساختار جایزه با موفقیت ویرایش شد');
      } else {
        await prizeStructureService.createPrizeStructure(payload);
        toast.success('ساختار جایزه با موفقیت ایجاد شد');
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'خطا در ذخیره ساختار جایزه');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            {prizeStructure ? 'ویرایش ساختار جایزه' : 'افزودن ساختار جایزه جدید'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <FormRow>
              <FormGroup>
                <Label>نام ساختار جایزه *</Label>
                <InputField
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="نام ساختار جایزه را وارد کنید"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>درصد سهم برگزارکننده *</Label>
                <InputField
                  type="number"
                  name="organizerCutPercent"
                  value={formData.organizerCutPercent}
                  onChange={handleChange}
                  placeholder="درصد سهم برگزارکننده"
                  min="0"
                  max="100"
                  required
                />
              </FormGroup>
            </FormRow>

            <PlacementsSection>
              <PlacementsHeader>
                <PlacementsTitle>
                  <Target size={16} />
                  رتبه‌بندی جوایز
                </PlacementsTitle>
                <AddPlacementButton type="button" onClick={addPlacement}>
                  <Plus size={14} />
                  افزودن رتبه
                </AddPlacementButton>
              </PlacementsHeader>

              <PlacementsTable>
                <TableHeader>
                  <TableHeaderCell>رتبه</TableHeaderCell>
                  <TableHeaderCell>درصد</TableHeaderCell>
                  <TableHeaderCell>امتیاز</TableHeaderCell>
                  <TableHeaderCell>عملیات</TableHeaderCell>
                  <TableHeaderCell></TableHeaderCell>
                </TableHeader>
                
                {formData.placements.map((placement, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <PlacementInput
                        type="number"
                        value={placement.row}
                        onChange={(e) => handlePlacementChange(index, 'row', e.target.value)}
                        placeholder="رتبه"
                        min="1"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <PlacementInput
                        type="number"
                        value={placement.percent}
                        onChange={(e) => handlePlacementChange(index, 'percent', e.target.value)}
                        placeholder="درصد"
                        min="0"
                        max="100"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <PlacementInput
                        type="number"
                        value={placement.score}
                        onChange={(e) => handlePlacementChange(index, 'score', e.target.value)}
                        placeholder="امتیاز"
                        min="0"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ color: '#b8b8d1', fontSize: '12px' }}>
                        {placement.row && placement.percent && placement.score ? 
                          `${placement.row}ام: ${placement.percent}%` : 
                          'نامکمل'
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <RemoveButton
                        type="button"
                        onClick={() => removePlacement(index)}
                        disabled={formData.placements.length === 1}
                      >
                        <Trash2 size={12} />
                      </RemoveButton>
                    </TableCell>
                  </TableRow>
                ))}
              </PlacementsTable>

              <TotalPercent>
                <TotalPercentText>مجموع درصدها:</TotalPercentText>
                <TotalPercentValue isValid={totalPercent === 100}>
                  {totalPercent}%
                </TotalPercentValue>
                {totalPercent !== 100 && (
                  <div style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '5px' }}>
                    مجموع باید 100% باشد
                  </div>
                )}
              </TotalPercent>
            </PlacementsSection>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={loading}
            icon={<X size={16} />}
          >
            انصراف
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || !isValid}
            icon={<Trophy size={16} />}
          >
            {loading ? 'در حال ذخیره...' : (prizeStructure ? 'ویرایش ساختار' : 'ایجاد ساختار')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PrizeStructureModal;
